import type { Lang } from "@/content/coaches";

export type { Lang };

// Display-only USD→EUR anchor. Defined in a leaf module to avoid circular
// imports with microservices-register.generated.ts. Keep in sync with
// scripts/ingest-microservices-register.mjs.
export { USD_TO_EUR, usdToEur } from "./microservices-pricing";

export interface PricingTier {
  name: { en: string; fr: string };
  price_xof: string;
  price_eur: string;
  features: { en: string[]; fr: string[] };
  featured?: boolean;
}

export interface MicroserviceFAQ {
  q: { en: string; fr: string };
  a: { en: string; fr: string };
}

export type MicroserviceCategory =
  | "branding-design"
  | "web-mobile"
  | "marketing-acquisition"
  | "content-seo"
  | "video-creative"
  | "product-ux"
  | "data-ai"
  | "ops-automation"
  | "legal-compliance"
  | "finance-fundraising"
  | "sales-cs"
  | "hr-talent"
  | "payments-uemoa";

export interface MicroserviceTier {
  price_usd: { min: number; max: number };
  price_eur?: { min: number; max: number };
  lead_days: { min: number; max: number };
  scope: { en: string; fr: string };
}

export interface MicroserviceTiers {
  basic: MicroserviceTier;
  standard: MicroserviceTier;
  premium: MicroserviceTier;
}

export interface Microservice {
  id: string;
  slug: string;
  name: { en: string; fr: string };
  tagline: { en: string; fr: string };
  description: { en: string; fr: string };
  category: MicroserviceCategory;
  pricing_model: "per_use" | "per_month" | "per_transaction";
  pricing_xof: string;
  pricing_eur: string;
  target_countries: ("SN" | "CI" | "ML" | "BF" | "BJ" | "TG" | "NE" | "GW" | "UEMOA-all")[];
  icon: string;
  featured: boolean;
  // Detail-page fields
  how_it_works: { en: string[]; fr: string[] };
  integrations: string[];
  faq: MicroserviceFAQ[];
  pricing_tiers?: PricingTier[];
  requires_scoping_call: boolean;
  // Extended fields (register-aligned, all optional for backwards compatibility)
  tiers?: MicroserviceTiers;
  prompt_announce?: { en: string; fr: string };
  card_brief?: { en: string; fr: string };
  value_prop?: { en: string; fr: string };
  deliverable?: { en: string; fr: string };
  visual_brief?: { en: string; fr: string };
  subscription_available?: boolean;
}

export const microservices: Microservice[] = [
  {
    id: "ms-01", slug: "wave-orange-payouts-api",
    name: {
      en: "Wave + Orange Money payouts API",
      fr: "API de paiements Wave + Orange Money"
    },
    tagline: {
      en: "One API for Wave, Orange Money, MTN MoMo, Moov — across all UEMOA",
      fr: "Une seule API pour Wave, Orange Money, MTN MoMo et Moov — dans toute la zone UEMOA"
    },
    description: {
      en: "Single unified API to send payouts to mobile money wallets across the 8 UEMOA countries. Supports Wave (70% market share in Côte d'Ivoire, 8M+ users in Senegal), Orange Money, MTN Mobile Money, Moov. Handles settlement, receipt generation, automatic reconciliation, and the 7.2% Ivorian mobile money tax where applicable. Stop building 4 integrations — use one.",
      fr: "API unifiée pour envoyer des paiements vers les portefeuilles mobile money dans les 8 pays UEMOA. Supporte Wave (70% de part de marché en Côte d'Ivoire, plus de 8 millions d'utilisateurs au Sénégal), Orange Money, MTN Mobile Money et Moov. Gère le règlement, la génération des reçus, le rapprochement automatique, et la taxe ivoirienne de 7,2% sur le mobile money lorsqu'elle s'applique. Arrêtez de coder 4 intégrations séparées — utilisez-en une seule."
    },
    category: "payments-uemoa", pricing_model: "per_transaction",
    pricing_xof: "150 XOF + 0,8%", pricing_eur: "≈ 0,23 € + 0,8%",
    target_countries: ["UEMOA-all"],
    icon: "Smartphone", featured: true,
    how_it_works: {
      en: [
        "Open your account and get API keys in 5 minutes",
        "Integrate our SDK (JS, Python, PHP) into your application",
        "Send payments to any UEMOA mobile money wallet",
        "Receive confirmation and automatic reconciliation webhooks"
      ],
      fr: [
        "Créez votre compte et obtenez vos clés API en 5 minutes",
        "Intégrez notre SDK (JS, Python, PHP) dans votre application",
        "Envoyez des paiements vers n'importe quel portefeuille mobile money UEMOA",
        "Recevez les webhooks de confirmation et de rapprochement automatique"
      ]
    },
    integrations: ["Wave", "Orange Money", "MTN MoMo", "Moov Money", "GIM-UEMOA"],
    faq: [
      {
        q: {
          en: "Do I need a separate account with each mobile money operator?",
          fr: "Dois-je avoir un compte séparé avec chaque opérateur mobile money ?"
        },
        a: {
          en: "No. Our platform consolidates Wave, Orange Money, MTN MoMo, and Moov behind a single API. One contract, one invoice, one integration.",
          fr: "Non. Notre plateforme consolide Wave, Orange Money, MTN MoMo et Moov derrière une seule API. Un seul contrat, une seule facturation, une seule intégration."
        }
      },
      {
        q: {
          en: "How does reconciliation with my accounting work?",
          fr: "Comment fonctionne le rapprochement avec ma comptabilité ?"
        },
        a: {
          en: "Each transaction generates a structured receipt exportable as CSV or directly importable into your SYSCOHADA accounting software.",
          fr: "Chaque transaction génère un reçu structuré exportable en CSV ou directement importable dans votre logiciel comptable SYSCOHADA."
        }
      },
      {
        q: {
          en: "What about the 7.2% Ivorian mobile money tax?",
          fr: "Qu'en est-il de la taxe ivoirienne de 7,2% sur mobile money ?"
        },
        a: {
          en: "Calculated and withheld automatically on Côte d'Ivoire transactions. Visible on receipts, traceable in exports.",
          fr: "Calculée et prélevée automatiquement sur les transactions en Côte d'Ivoire. Visible dans les reçus, traçable dans les exports."
        }
      }
    ],
    requires_scoping_call: true
  },
  {
    id: "ms-02", slug: "pi-spi-integration",
    name: {
      en: "BCEAO PI-SPI integration",
      fr: "Intégration PI-SPI BCEAO"
    },
    tagline: {
      en: "Instant cross-border transfers across all 8 UEMOA countries",
      fr: "Virements instantanés cross-border dans les 8 pays UEMOA"
    },
    description: {
      en: "Ready-to-deploy integration with PI-SPI, BCEAO's interoperable instant payment platform. Connect to 50+ banks, microfinance institutions, and mobile wallets across UEMOA from a single endpoint. Real-time cross-border transfers — Togo to Côte d'Ivoire, Senegal to Mali — without correspondent banking friction. Compliance-ready ahead of the June 30, 2026 BCEAO mandatory connection deadline.",
      fr: "Intégration prête à déployer avec PI-SPI, la plateforme de paiements instantanés interopérable de la BCEAO. Connectez-vous à plus de 50 banques, institutions de microfinance et portefeuilles mobiles UEMOA depuis un seul point d'entrée. Virements cross-border en temps réel — Togo vers Côte d'Ivoire, Sénégal vers Mali — sans passer par la banque correspondante. Conformité assurée avant l'échéance obligatoire BCEAO du 30 juin 2026."
    },
    category: "payments-uemoa", pricing_model: "per_month",
    pricing_xof: "49 000 XOF/mois", pricing_eur: "≈ 75 €/mois",
    target_countries: ["UEMOA-all"],
    icon: "Network", featured: true,
    how_it_works: {
      en: [
        "Initial scoping call to map your current banking and rails",
        "We provision your PI-SPI sandbox endpoint within 5 business days",
        "Joint test plan against the BCEAO certification scenarios",
        "Production go-live with monitoring and 24/7 support"
      ],
      fr: [
        "Appel de cadrage initial pour cartographier vos rails bancaires actuels",
        "Nous provisionnons votre point d'entrée PI-SPI sandbox sous 5 jours ouvrés",
        "Plan de tests conjoint contre les scénarios de certification BCEAO",
        "Mise en production avec monitoring et support 24/7"
      ]
    },
    integrations: ["BCEAO PI-SPI", "Ecobank", "Orabank", "Bank of Africa", "Coris Bank", "GIM-UEMOA"],
    faq: [
      {
        q: {
          en: "Do I have to be a licensed financial institution?",
          fr: "Dois-je être un établissement financier agréé ?"
        },
        a: {
          en: "PI-SPI access requires either a banking, EME, or PSP license. We help you map the right path for your situation during the scoping call.",
          fr: "L'accès PI-SPI exige soit un agrément bancaire, soit un agrément EME, soit un agrément PSP. Nous vous aidons à cartographier le bon parcours pour votre situation lors de l'appel de cadrage."
        }
      },
      {
        q: {
          en: "What is the June 30, 2026 deadline?",
          fr: "Quelle est l'échéance du 30 juin 2026 ?"
        },
        a: {
          en: "BCEAO has mandated that all eligible financial institutions must be connected to PI-SPI by June 30, 2026. Starting now leaves comfortable runway for certification.",
          fr: "La BCEAO a rendu obligatoire la connexion à PI-SPI pour toutes les institutions financières éligibles au 30 juin 2026. Démarrer maintenant laisse une marge confortable pour la certification."
        }
      },
      {
        q: {
          en: "Can I send to mobile wallets, not just bank accounts?",
          fr: "Puis-je envoyer vers des portefeuilles mobiles, pas seulement des comptes bancaires ?"
        },
        a: {
          en: "Yes. PI-SPI is interoperable — any participating wallet or bank in any UEMOA country is reachable from a single endpoint.",
          fr: "Oui. PI-SPI est interopérable — n'importe quel portefeuille ou banque participant dans n'importe quel pays UEMOA est joignable depuis un point d'entrée unique."
        }
      }
    ],
    requires_scoping_call: true
  },
  {
    id: "ms-03", slug: "syscohada-bookkeeping",
    name: {
      en: "SYSCOHADA-compliant bookkeeping",
      fr: "Tenue de comptes conforme SYSCOHADA"
    },
    tagline: {
      en: "Audit-ready books in the format banks and VCs actually require",
      fr: "Comptes prêts pour l'audit dans le format exigé par les banques et les investisseurs"
    },
    description: {
      en: "Structured bookkeeping conforming to SYSCOHADA — the accounting standard mandatory across all OHADA countries for bank credit applications, tax filings, and investor reporting. Chart of accounts pre-configured per OHADA articles. Connects to Ecobank, Orabank, Bank of Africa, major microfinance institutions. Generates bilan, compte de résultat, and TAFIRE ready for ONECCA-certified expert-comptable sign-off.",
      fr: "Tenue de comptes structurée conforme au SYSCOHADA — la norme comptable obligatoire dans tous les pays OHADA pour les dossiers de crédit bancaire, les déclarations fiscales, et le reporting investisseurs. Plan comptable pré-paramétré selon les articles OHADA. Se connecte à Ecobank, Orabank, Bank of Africa, et aux principales institutions de microfinance. Génère bilan, compte de résultat et TAFIRE prêts pour signature par un expert-comptable agréé ONECCA."
    },
    category: "finance-fundraising", pricing_model: "per_month",
    pricing_xof: "59 000 XOF/mois", pricing_eur: "≈ 90 €/mois",
    target_countries: ["UEMOA-all"],
    icon: "BookOpen", featured: true,
    how_it_works: {
      en: [
        "Connect your bank accounts and accept the OHADA chart of accounts",
        "Daily auto-categorization with manual override on edge cases",
        "Monthly close in 48h with bilan, compte de résultat, TAFIRE",
        "Expert-comptable ONECCA review and sign-off included on Growth and Scale tiers"
      ],
      fr: [
        "Connectez vos comptes bancaires et acceptez le plan comptable OHADA",
        "Catégorisation automatique quotidienne avec correction manuelle des cas particuliers",
        "Clôture mensuelle sous 48h avec bilan, compte de résultat, TAFIRE",
        "Revue et signature par expert-comptable agréé ONECCA inclus dès l'offre Growth"
      ]
    },
    integrations: ["Ecobank", "Orabank", "Bank of Africa", "Coris Bank", "Wave", "Orange Money", "ONECCA"],
    faq: [
      {
        q: {
          en: "Can my own expert-comptable validate the books?",
          fr: "Mon propre expert-comptable peut-il valider les comptes ?"
        },
        a: {
          en: "Yes. The export format follows SYSCOHADA so any ONECCA-certified expert-comptable can review and sign without rework.",
          fr: "Oui. Le format d'export suit SYSCOHADA, tout expert-comptable agréé ONECCA peut donc réviser et signer sans retraitement."
        }
      },
      {
        q: {
          en: "Does this replace my accountant?",
          fr: "Cela remplace-t-il mon comptable ?"
        },
        a: {
          en: "No. It structures their work so they spend hours instead of days each month. The Scale tier includes an in-house expert-comptable.",
          fr: "Non. Cela structure leur travail pour qu'ils y consacrent des heures au lieu de jours chaque mois. L'offre Scale inclut un expert-comptable en interne."
        }
      },
      {
        q: {
          en: "Which UEMOA countries are covered?",
          fr: "Quels pays UEMOA sont couverts ?"
        },
        a: {
          en: "All 8 OHADA member states in UEMOA. Country-specific overlays handle local tax filings.",
          fr: "Les 8 États membres OHADA en UEMOA. Des surcouches spécifiques par pays gèrent les déclarations fiscales locales."
        }
      }
    ],
    pricing_tiers: [
      {
        name: { en: "Starter", fr: "Starter" },
        price_xof: "59 000 XOF/mois", price_eur: "≈ 90 €/mois",
        features: {
          en: ["Up to 100 transactions/month", "Monthly close in 48h", "Bilan + compte de résultat", "Email support"],
          fr: ["Jusqu'à 100 transactions/mois", "Clôture mensuelle en 48h", "Bilan + compte de résultat", "Support par email"]
        }
      },
      {
        name: { en: "Growth", fr: "Growth" },
        price_xof: "149 000 XOF/mois", price_eur: "≈ 230 €/mois",
        features: {
          en: ["Up to 500 transactions/month", "TAFIRE included", "Expert-comptable ONECCA review", "Investor-ready monthly pack"],
          fr: ["Jusqu'à 500 transactions/mois", "TAFIRE inclus", "Revue expert-comptable agréé ONECCA", "Pack mensuel prêt-investisseur"]
        },
        featured: true
      },
      {
        name: { en: "Scale", fr: "Scale" },
        price_xof: "395 000 XOF/mois", price_eur: "≈ 600 €/mois",
        features: {
          en: ["Unlimited transactions", "Dedicated expert-comptable", "Multi-entity consolidation", "Audit support"],
          fr: ["Transactions illimitées", "Expert-comptable dédié", "Consolidation multi-entités", "Accompagnement audit"]
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-04", slug: "bceao-credit-dossier",
    name: {
      en: "BCEAO credit application dossier",
      fr: "Dossier de crédit BCEAO"
    },
    tagline: {
      en: "Bank-ready financing dossier with FAGACE and FSA guarantee pathways",
      fr: "Dossier de financement prêt-banque avec parcours de garantie FAGACE et FSA"
    },
    description: {
      en: "Complete credit application package structured to the standards UEMOA commercial banks actually require: SYSCOHADA-conforming business plan, 3-year financial projections with sensitivity analysis, and direct pathways to FAGACE and FSA guarantee schemes. These guarantees cover 50-80% of loan amounts — critical when most UEMOA banks require 150-200% collateral coverage.",
      fr: "Package complet de demande de crédit structuré selon les standards réellement exigés par les banques commerciales UEMOA : business plan conforme SYSCOHADA, projections financières à 3 ans avec analyse de sensibilité, et parcours directs vers les mécanismes de garantie FAGACE (Fonds Africain de Garantie et de Coopération Économique) et FSA (Fonds de Solidarité Africain). Ces garanties couvrent 50 à 80% des montants empruntés — décisif quand la plupart des banques UEMOA exigent 150 à 200% de couverture en garanties réelles."
    },
    category: "finance-fundraising", pricing_model: "per_use",
    pricing_xof: "195 000 XOF", pricing_eur: "≈ 297 € forfait",
    target_countries: ["UEMOA-all"],
    icon: "FileCheck", featured: true,
    how_it_works: {
      en: [
        "Intake form: business overview, requested amount, intended use",
        "Working session to build the SYSCOHADA-conforming business plan",
        "We assemble the full dossier and route it to FAGACE / FSA as applicable",
        "Bank shortlist and warm intro to credit officers we know"
      ],
      fr: [
        "Formulaire d'admission : présentation de l'activité, montant demandé, usage prévu",
        "Atelier pour construire le business plan conforme SYSCOHADA",
        "Nous assemblons le dossier complet et le routons vers FAGACE / FSA selon les cas",
        "Shortlist de banques et mise en relation chaleureuse avec des chargés de crédit identifiés"
      ]
    },
    integrations: ["FAGACE", "FSA", "Ecobank", "Orabank", "Bank of Africa", "BOAD"],
    faq: [
      {
        q: {
          en: "Do you guarantee the loan will be approved?",
          fr: "Garantissez-vous l'obtention du prêt ?"
        },
        a: {
          en: "No — credit decision is the bank's. We dramatically improve odds by structuring the dossier to the standard banks expect and by routing to guarantee schemes.",
          fr: "Non — la décision de crédit appartient à la banque. Nous améliorons fortement les chances en structurant le dossier au standard attendu et en routant vers les mécanismes de garantie."
        }
      },
      {
        q: {
          en: "How long does it take?",
          fr: "Combien de temps cela prend-il ?"
        },
        a: {
          en: "Dossier ready in 2-3 weeks. Bank decision typically 4-8 weeks after submission depending on the institution.",
          fr: "Dossier prêt en 2 à 3 semaines. Décision bancaire généralement 4 à 8 semaines après soumission selon l'établissement."
        }
      },
      {
        q: {
          en: "What is the FAGACE guarantee?",
          fr: "Qu'est-ce que la garantie FAGACE ?"
        },
        a: {
          en: "FAGACE covers 50-80% of UEMOA SME loan principal, reducing collateral requirements dramatically. Underused because most founders don't know how to apply.",
          fr: "FAGACE couvre 50 à 80% du principal des prêts PME en UEMOA, réduisant fortement les exigences de garantie. Sous-utilisée car la plupart des dirigeants ne savent pas comment y accéder."
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-05", slug: "ohada-company-formation",
    name: {
      en: "OHADA company formation assistant",
      fr: "Assistant de création d'entreprise OHADA"
    },
    tagline: {
      en: "Incorporate SARL, SA, or SAS in any OHADA country in under 2 weeks",
      fr: "Créer une SARL, SA ou SAS dans tout pays OHADA en moins de 2 semaines"
    },
    description: {
      en: "Guided workflow to incorporate a company under the OHADA Uniform Act on Commercial Companies — unified business law applying across 8 UEMOA countries plus 9 other African states. Handles statuts drafting, RCCM registration, NINEA/IFU registration, corporate bank account opening. Country overlays for Côte d'Ivoire (CEPICI), Senegal (APIX), Togo (CFE), Bénin (GUFE). Launch fast, expand to other OHADA markets without re-incorporating.",
      fr: "Parcours guidé pour créer une société sous l'Acte Uniforme OHADA relatif aux Sociétés Commerciales — le droit des affaires unifié applicable dans les 8 pays de l'UEMOA et 9 autres États africains. Gère la rédaction des statuts, l'immatriculation au RCCM, l'enregistrement au NINEA ou à l'IFU selon le pays, et l'ouverture du compte bancaire société. Surcouches spécifiques pour la Côte d'Ivoire (CEPICI), le Sénégal (APIX), le Togo (CFE) et le Bénin (GUFE). Démarrez rapidement, développez-vous vers d'autres marchés OHADA sans recréer une société."
    },
    category: "legal-compliance", pricing_model: "per_use",
    pricing_xof: "295 000 XOF", pricing_eur: "≈ 450 € forfait",
    target_countries: ["UEMOA-all"],
    icon: "Scale", featured: true,
    how_it_works: {
      en: [
        "Pick your country and entity type (SARL, SA, SAS)",
        "Complete the guided form — partners, capital, business activity",
        "We draft statuts and lodge RCCM + tax registrations",
        "Bank account opening with our partner network in 5-10 days"
      ],
      fr: [
        "Choisissez votre pays et la forme juridique (SARL, SA, SAS)",
        "Remplissez le formulaire guidé — associés, capital, activité",
        "Nous rédigeons les statuts et déposons l'immatriculation RCCM et fiscale",
        "Ouverture du compte bancaire via notre réseau partenaire sous 5 à 10 jours"
      ]
    },
    integrations: ["CEPICI (CI)", "APIX (SN)", "CFE (TG)", "GUFE (BJ)", "RCCM", "Ecobank", "Orabank"],
    faq: [
      {
        q: {
          en: "Which entity type should I pick?",
          fr: "Quelle forme juridique choisir ?"
        },
        a: {
          en: "SARL is the default for most operating companies (low capital, simple governance). SA suits regulated activities. SAS offers maximum flexibility but is newer in UEMOA — confirm availability per country.",
          fr: "La SARL est le choix par défaut pour la plupart des sociétés opérationnelles (capital faible, gouvernance simple). La SA convient aux activités réglementées. La SAS offre la flexibilité maximale mais est plus récente en UEMOA — confirmez la disponibilité selon le pays."
        }
      },
      {
        q: {
          en: "Can I incorporate without being physically present?",
          fr: "Puis-je créer la société sans être physiquement présent ?"
        },
        a: {
          en: "In most countries yes, via notarized power of attorney. Côte d'Ivoire and Senegal accept this; Togo requires a brief in-person step.",
          fr: "Dans la plupart des pays oui, via procuration notariée. La Côte d'Ivoire et le Sénégal l'acceptent ; le Togo exige une brève étape en présentiel."
        }
      },
      {
        q: {
          en: "What about expansion to another OHADA country?",
          fr: "Et l'expansion vers un autre pays OHADA ?"
        },
        a: {
          en: "Once incorporated under OHADA, you can register a branch in any other member state without forming a new entity.",
          fr: "Une fois immatriculée OHADA, vous pouvez enregistrer une succursale dans tout autre État membre sans créer une nouvelle entité."
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-06", slug: "whatsapp-fr-commerce",
    name: {
      en: "WhatsApp commerce — FR optimized",
      fr: "Commerce WhatsApp — optimisé pour le marché francophone"
    },
    tagline: {
      en: "Catalog + Wave/Orange payment links, in French and local languages",
      fr: "Catalogue et liens de paiement Wave/Orange, en français et langues locales"
    },
    description: {
      en: "WhatsApp Business API integration tuned for Francophone West African commerce patterns. Product catalog, order flow, payment collection via Wave and Orange Money links (not Stripe, which doesn't work cleanly in UEMOA). AI auto-responder supports French plus Wolof, Bambara, Dioula, and Fon greetings. Priced for local purchasing power.",
      fr: "Intégration de l'API WhatsApp Business ajustée aux pratiques commerciales d'Afrique de l'Ouest francophone. Catalogue produits, tunnel de commande, et encaissement via liens Wave et Orange Money (et non Stripe, qui ne fonctionne pas correctement en zone UEMOA). Agent conversationnel IA supportant le français ainsi que le wolof, le bambara, le dioula et les formules d'accueil en fon. Tarif aligné sur le pouvoir d'achat local."
    },
    category: "sales-cs", pricing_model: "per_month",
    pricing_xof: "25 000 XOF/mois + frais WA", pricing_eur: "≈ 38 €/mois + frais WA",
    target_countries: ["UEMOA-all"],
    icon: "MessageCircle", featured: true,
    how_it_works: {
      en: [
        "Connect your WhatsApp Business number (or get one provisioned)",
        "Upload your catalog — we structure it for WhatsApp shopping",
        "Auto-responder runs in French + your chosen local languages",
        "Orders convert to Wave / Orange Money payment links automatically"
      ],
      fr: [
        "Connectez votre numéro WhatsApp Business (ou obtenez-en un)",
        "Chargez votre catalogue — nous le structurons pour WhatsApp shopping",
        "L'agent conversationnel répond en français et dans vos langues locales",
        "Les commandes deviennent des liens de paiement Wave / Orange Money automatiquement"
      ]
    },
    integrations: ["WhatsApp Business API", "Wave", "Orange Money", "MTN MoMo"],
    faq: [
      {
        q: {
          en: "Why not Stripe for payments?",
          fr: "Pourquoi pas Stripe pour les paiements ?"
        },
        a: {
          en: "Stripe is not natively supported in UEMOA, and most consumers don't have international cards. Wave and Orange Money handle 80%+ of digital transactions in the region.",
          fr: "Stripe n'est pas supporté nativement en UEMOA, et la plupart des consommateurs n'ont pas de carte internationale. Wave et Orange Money traitent plus de 80% des transactions numériques dans la région."
        }
      },
      {
        q: {
          en: "Which local languages are supported?",
          fr: "Quelles langues locales sont supportées ?"
        },
        a: {
          en: "Wolof, Bambara, Dioula, and Fon greeting + intent recognition. Full conversation in French.",
          fr: "Wolof, bambara, dioula et fon : reconnaissance des formules d'accueil et des intentions. Conversation complète en français."
        }
      },
      {
        q: {
          en: "What are the WA fees?",
          fr: "Quels sont les frais WhatsApp ?"
        },
        a: {
          en: "WhatsApp charges per conversation (around €0.005-€0.05 depending on type). Billed transparently on your monthly invoice.",
          fr: "WhatsApp facture à la conversation (environ 0,005 € à 0,05 € selon le type). Facturé de manière transparente sur votre facture mensuelle."
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-07", slug: "gim-uemoa-card-acceptance",
    name: {
      en: "GIM-UEMOA card acceptance",
      fr: "Acceptation de cartes GIM-UEMOA"
    },
    tagline: {
      en: "Accept GIM cards, Visa, Mastercard — online and in-person",
      fr: "Acceptez les cartes GIM, Visa, Mastercard — en ligne comme en magasin"
    },
    description: {
      en: "Payment acceptance integrated with GIM-UEMOA, the regional interbank switch connecting 150+ banks, postal services, microfinance institutions, and fintechs across the 8 member countries. Supports Verve and Mastercard interoperability (2024 expansion), enabling card acceptance from 120+ million cardholders region-wide. Covers online checkout and in-person POS.",
      fr: "Acceptation de paiements intégrée à GIM-UEMOA, le switch interbancaire régional reliant plus de 150 banques, services postaux, institutions de microfinance et fintechs dans les 8 pays membres. Supporte l'interopérabilité Verve et Mastercard (expansion 2024), permettant l'acceptation des cartes de plus de 120 millions de porteurs dans la région. Couvre le checkout en ligne et le TPE physique."
    },
    category: "payments-uemoa", pricing_model: "per_transaction",
    pricing_xof: "1,8% par transaction", pricing_eur: "≈ 1,8% par transaction",
    target_countries: ["UEMOA-all"],
    icon: "CreditCard", featured: false,
    how_it_works: {
      en: [
        "Scoping call to confirm KYC profile and merchant category",
        "GIM-UEMOA acquiring agreement signed via our partner bank",
        "Online: hosted checkout in 7 days. POS: terminal shipped in 14 days",
        "Daily settlement to your operating account"
      ],
      fr: [
        "Appel de cadrage pour confirmer le profil KYC et la catégorie marchand",
        "Contrat d'acquisition GIM-UEMOA signé via notre banque partenaire",
        "En ligne : checkout hébergé en 7 jours. TPE : terminal livré en 14 jours",
        "Règlement quotidien sur votre compte d'exploitation"
      ]
    },
    integrations: ["GIM-UEMOA", "Visa", "Mastercard", "Verve", "Ecobank", "Coris Bank"],
    faq: [
      {
        q: {
          en: "Do I need a bank account in UEMOA?",
          fr: "Dois-je avoir un compte bancaire en zone UEMOA ?"
        },
        a: {
          en: "Yes, settlement is to a UEMOA-domiciled commercial account. We can help open one if needed.",
          fr: "Oui, le règlement se fait sur un compte commercial domicilié UEMOA. Nous pouvons aider à en ouvrir un si nécessaire."
        }
      },
      {
        q: {
          en: "What is the merchant approval timeline?",
          fr: "Quel est le délai d'agrément marchand ?"
        },
        a: {
          en: "Typically 2-3 weeks once KYC documents are complete. We pre-package them to avoid back-and-forth.",
          fr: "Typiquement 2 à 3 semaines une fois les documents KYC complets. Nous les pré-empaquetons pour éviter les allers-retours."
        }
      },
      {
        q: {
          en: "Are international cards supported?",
          fr: "Les cartes internationales sont-elles supportées ?"
        },
        a: {
          en: "Yes — Visa and Mastercard are interoperable on GIM-UEMOA. Verve cards (Nigerian) are also accepted since the 2024 expansion.",
          fr: "Oui — Visa et Mastercard sont interopérables sur GIM-UEMOA. Les cartes Verve (Nigeria) sont également acceptées depuis l'expansion 2024."
        }
      }
    ],
    requires_scoping_call: true
  },
  {
    id: "ms-08", slug: "uemoa-investor-map",
    name: {
      en: "UEMOA investor map",
      fr: "Cartographie des investisseurs UEMOA"
    },
    tagline: {
      en: "60+ Francophone-focused VCs, angels, and grant programs",
      fr: "Plus de 60 fonds, business angels et programmes de subvention orientés francophone"
    },
    description: {
      en: "Curated directory of 60+ active UEMOA investors: Partech Africa, Janngo, Orange Ventures, Teranga Capital, Comoé Capital, Wuri Ventures, plus FAGACE, FSA, and BCEAO-backed programs. Each entry includes ticket size, thesis, portfolio, recent activity, and warm-intro paths via your LinkedIn graph. Covers the Tremplin Startup UEMOA competition (up to 14M XOF per winner) and state pipelines like CEPICI, ADEPME, APIX.",
      fr: "Annuaire curé de plus de 60 investisseurs actifs en zone UEMOA : Partech Africa, Janngo, Orange Ventures, Teranga Capital, Comoé Capital, Wuri Ventures, ainsi que FAGACE, FSA et les programmes adossés à la BCEAO. Chaque fiche inclut le ticket moyen, la thèse d'investissement, le portefeuille, l'activité récente, et les parcours de mise en relation via votre réseau LinkedIn. Couvre également le concours Tremplin Startup UEMOA (jusqu'à 14 millions de XOF par lauréat) et les pipelines publics comme le CEPICI, l'ADEPME, l'APIX."
    },
    category: "sales-cs", pricing_model: "per_month",
    pricing_xof: "19 000 XOF/mois", pricing_eur: "≈ 29 €/mois",
    target_countries: ["UEMOA-all"],
    icon: "Users", featured: false,
    how_it_works: {
      en: [
        "Activate your account and import your LinkedIn graph",
        "Browse fund profiles filtered by ticket, stage, sector, geography",
        "Identify warm-intro paths through your existing network",
        "Save fund shortlists and track outreach status"
      ],
      fr: [
        "Activez votre compte et importez votre réseau LinkedIn",
        "Parcourez les fiches investisseurs filtrées par ticket, stade, secteur, géographie",
        "Identifiez les chemins de mise en relation chaleureuse via votre réseau existant",
        "Sauvegardez vos shortlists de fonds et suivez l'état des prises de contact"
      ]
    },
    integrations: ["LinkedIn", "Partech Africa", "Janngo", "Orange Ventures", "Teranga Capital", "FAGACE", "FSA"],
    faq: [
      {
        q: {
          en: "Are the funds actively investing?",
          fr: "Les fonds investissent-ils activement ?"
        },
        a: {
          en: "Each profile is updated quarterly with last deal date and activity score. Inactive funds are flagged.",
          fr: "Chaque fiche est mise à jour trimestriellement avec la date du dernier deal et un score d'activité. Les fonds inactifs sont signalés."
        }
      },
      {
        q: {
          en: "Do you make introductions for me?",
          fr: "Faites-vous les introductions à ma place ?"
        },
        a: {
          en: "We surface paths via your network. The intro itself goes through your existing connection — far stronger than a cold pitch.",
          fr: "Nous identifions les chemins via votre réseau. L'introduction passe par votre relation existante — bien plus efficace qu'un pitch à froid."
        }
      },
      {
        q: {
          en: "What about non-VC funding?",
          fr: "Et le financement non-capital-risque ?"
        },
        a: {
          en: "FAGACE, FSA, BCEAO programs, Tremplin Startup UEMOA, and state pipelines (CEPICI, APIX, ADEPME) are all included.",
          fr: "FAGACE, FSA, programmes BCEAO, Tremplin Startup UEMOA, et pipelines publics (CEPICI, APIX, ADEPME) sont tous inclus."
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-09", slug: "uemoa-tax-compliance",
    name: {
      en: "UEMOA tax & regulatory filings",
      fr: "Déclarations fiscales et réglementaires UEMOA"
    },
    tagline: {
      en: "TVA, IS, ITS, BIC — filing templates across all 8 UEMOA jurisdictions",
      fr: "TVA, IS, ITS, BIC — modèles de déclaration dans les 8 juridictions UEMOA"
    },
    description: {
      en: "Pre-filled filing templates and monthly reminders for all UEMOA tax obligations: TVA (18% harmonized across most member states), Impôt sur les Sociétés, Impôt sur les Traitements et Salaires, Bénéfices Industriels et Commerciaux, the 7.2% Ivorian mobile money tax, and BCEAO regulatory reporting for EME-licensed operators. One dashboard, 8 countries. Doesn't replace your expert-comptable — structures their work so they spend 2 hours instead of 10.",
      fr: "Modèles de déclaration pré-remplis et rappels mensuels pour toutes les obligations fiscales UEMOA : TVA (18% harmonisée dans la plupart des États membres), Impôt sur les Sociétés, Impôt sur les Traitements et Salaires, Bénéfices Industriels et Commerciaux, taxe ivoirienne de 7,2% sur le mobile money, et reporting réglementaire BCEAO pour les opérateurs titulaires d'un agrément EME. Un seul tableau de bord, 8 pays. Ne remplace pas votre expert-comptable — structure son travail pour qu'il y consacre 2 heures au lieu de 10."
    },
    category: "legal-compliance", pricing_model: "per_month",
    pricing_xof: "45 000 XOF/mois par pays", pricing_eur: "≈ 69 €/mois par pays",
    target_countries: ["UEMOA-all"],
    icon: "ShieldCheck", featured: false,
    how_it_works: {
      en: [
        "Pick the countries where you operate",
        "Connect your bookkeeping (or sync from our SYSCOHADA service)",
        "Templates pre-fill each month — review and submit",
        "Calendar reminders for every filing deadline per country"
      ],
      fr: [
        "Choisissez les pays où vous opérez",
        "Connectez votre comptabilité (ou synchronisez depuis notre service SYSCOHADA)",
        "Les modèles se pré-remplissent chaque mois — relisez et soumettez",
        "Rappels calendaires pour chaque échéance par pays"
      ]
    },
    integrations: ["SYSCOHADA bookkeeping", "DGI Côte d'Ivoire", "DGID Sénégal", "DGI Mali", "DGI Bénin", "DGI Togo"],
    faq: [
      {
        q: {
          en: "Do you submit on my behalf?",
          fr: "Soumettez-vous à ma place ?"
        },
        a: {
          en: "No. We pre-fill and structure; your expert-comptable reviews and submits. Saves them hours of manual work each month.",
          fr: "Non. Nous pré-remplissons et structurons ; votre expert-comptable relit et soumet. Lui économise des heures de travail manuel chaque mois."
        }
      },
      {
        q: {
          en: "What if I miss a deadline?",
          fr: "Et si je rate une échéance ?"
        },
        a: {
          en: "Penalties depend on the country and tax. We send 3 escalating reminders to avoid this.",
          fr: "Les pénalités dépendent du pays et de l'impôt. Nous envoyons 3 rappels graduels pour éviter cela."
        }
      },
      {
        q: {
          en: "Is the 18% TVA harmonized?",
          fr: "La TVA à 18% est-elle harmonisée ?"
        },
        a: {
          en: "Most UEMOA states use 18% but exemptions and rates on specific goods/services vary. Country-specific overlays handle this.",
          fr: "La plupart des États UEMOA utilisent 18% mais les exonérations et taux sur biens et services spécifiques varient. Des surcouches par pays gèrent cela."
        }
      }
    ],
    pricing_tiers: [
      {
        name: { en: "Single country", fr: "Pays unique" },
        price_xof: "45 000 XOF/mois", price_eur: "≈ 69 €/mois",
        features: {
          en: ["1 country coverage", "Monthly templates", "Deadline reminders", "Email support"],
          fr: ["Couverture 1 pays", "Modèles mensuels", "Rappels d'échéances", "Support email"]
        }
      },
      {
        name: { en: "Multi-country", fr: "Multi-pays" },
        price_xof: "129 000 XOF/mois", price_eur: "≈ 197 €/mois",
        features: {
          en: ["Up to 4 countries", "Cross-country dashboard", "Priority support", "Quarterly compliance review"],
          fr: ["Jusqu'à 4 pays", "Tableau de bord cross-pays", "Support prioritaire", "Revue de conformité trimestrielle"]
        },
        featured: true
      },
      {
        name: { en: "Full UEMOA", fr: "UEMOA complet" },
        price_xof: "279 000 XOF/mois", price_eur: "≈ 425 €/mois",
        features: {
          en: ["All 8 UEMOA countries", "Dedicated compliance lead", "BCEAO reporting", "Audit support"],
          fr: ["Les 8 pays UEMOA", "Référent conformité dédié", "Reporting BCEAO", "Accompagnement audit"]
        }
      }
    ],
    requires_scoping_call: false
  },
  {
    id: "ms-10", slug: "uemoa-payroll-hiring",
    name: {
      en: "UEMOA payroll & hiring infrastructure",
      fr: "Infrastructure paie et recrutement UEMOA"
    },
    tagline: {
      en: "Hire compliantly in all 8 countries — CNSS, IPRES, social charges handled",
      fr: "Embaucher en conformité dans les 8 pays — CNSS, IPRES, charges sociales gérées"
    },
    description: {
      en: "Payroll and employment infrastructure covering the 8 UEMOA countries: calculation and filing of CNSS (Senegal, Mali, Burkina Faso), IPRES retirement contributions (Senegal), CNPS (Côte d'Ivoire, Togo, Bénin), ITS withholding, convention collective compliance per sector. Handles the specific nuances Deel and Remote.com don't — local convention collective rules, 13th-month conventions, sectoral minimum wage tables. Onboard an employee in Côte d'Ivoire in 48 hours, compliantly.",
      fr: "Infrastructure de paie et d'emploi couvrant les 8 pays de l'UEMOA : calcul et déclaration CNSS (Sénégal, Mali, Burkina Faso), cotisations retraite IPRES (Sénégal), CNPS (Côte d'Ivoire, Togo, Bénin), retenue ITS, conformité conventions collectives par secteur. Gère les subtilités locales que Deel et Remote.com ne couvrent pas — règles des conventions collectives, conventions du 13ème mois, grilles de SMIG sectoriels. Onboardez un salarié en Côte d'Ivoire en 48 heures, en toute conformité."
    },
    category: "ops-automation", pricing_model: "per_month",
    pricing_xof: "9 800 XOF/salarié/mois", pricing_eur: "≈ 15 €/salarié/mois",
    target_countries: ["UEMOA-all"],
    icon: "Globe", featured: false,
    how_it_works: {
      en: [
        "Pick the countries you hire in and confirm sector convention",
        "Add employees with their gross salary — we compute net + charges",
        "Monthly payroll runs auto-generated, filings prepared",
        "Pay employees via Wave, Orange Money, or bank transfer"
      ],
      fr: [
        "Choisissez les pays de recrutement et confirmez la convention sectorielle",
        "Ajoutez les salariés avec leur brut — nous calculons net et charges",
        "Bulletins de paie mensuels générés automatiquement, déclarations préparées",
        "Versez les salaires via Wave, Orange Money ou virement bancaire"
      ]
    },
    integrations: ["CNSS", "IPRES", "CNPS", "Wave", "Orange Money", "Ecobank"],
    faq: [
      {
        q: {
          en: "How does this differ from Deel or Remote.com?",
          fr: "En quoi cela diffère-t-il de Deel ou Remote.com ?"
        },
        a: {
          en: "Deel and Remote handle the global pieces but miss the UEMOA-specific layer: convention collective rules, IPRES, sectoral SMIG tables, and the nuance of CNSS vs CNPS by country.",
          fr: "Deel et Remote gèrent la couche globale mais ratent la couche spécifique UEMOA : conventions collectives, IPRES, grilles SMIG sectorielles, et la nuance CNSS / CNPS selon le pays."
        }
      },
      {
        q: {
          en: "Can I hire across multiple UEMOA countries?",
          fr: "Puis-je recruter dans plusieurs pays UEMOA ?"
        },
        a: {
          en: "Yes — one dashboard, 8 country payroll engines. Each employee is registered under the correct local regime.",
          fr: "Oui — un seul tableau de bord, 8 moteurs de paie. Chaque salarié est rattaché au bon régime local."
        }
      },
      {
        q: {
          en: "Do you handle the 13th month?",
          fr: "Gérez-vous le 13ème mois ?"
        },
        a: {
          en: "Yes, where the convention collective requires it — provisioned monthly and paid out per the local calendar.",
          fr: "Oui, lorsque la convention collective l'exige — provisionné mensuellement et versé selon le calendrier local."
        }
      }
    ],
    pricing_tiers: [
      {
        name: { en: "Starter", fr: "Starter" },
        price_xof: "9 800 XOF/salarié/mois", price_eur: "≈ 15 €/salarié/mois",
        features: {
          en: ["Up to 10 employees", "Single country", "Monthly payslips", "CNSS/IPRES filings"],
          fr: ["Jusqu'à 10 salariés", "Pays unique", "Bulletins mensuels", "Déclarations CNSS/IPRES"]
        }
      },
      {
        name: { en: "Growth", fr: "Growth" },
        price_xof: "8 200 XOF/salarié/mois", price_eur: "≈ 12,50 €/salarié/mois",
        features: {
          en: ["Up to 50 employees", "Multi-country (up to 3)", "Wave/Orange salary disbursement", "HR support included"],
          fr: ["Jusqu'à 50 salariés", "Multi-pays (jusqu'à 3)", "Versement via Wave/Orange", "Support RH inclus"]
        },
        featured: true
      },
      {
        name: { en: "Scale", fr: "Scale" },
        price_xof: "6 500 XOF/salarié/mois", price_eur: "≈ 10 €/salarié/mois",
        features: {
          en: ["Unlimited employees", "All 8 UEMOA countries", "Dedicated payroll lead", "API access"],
          fr: ["Salariés illimités", "Les 8 pays UEMOA", "Référent paie dédié", "Accès API"]
        }
      }
    ],
    requires_scoping_call: false
  }
];

// ============================================================================
// Founder microservices register — Phase A (12 sample rows, one per category)
// Source: uploaded register (FR/EN). Pricing in USD ranges B/S/P (Basic/Standard/Premium).
// ============================================================================

const usd = (min: number, max: number) => `$${min}–$${max}`;
const eurFrom = (usdMin: number) => `≈ ${Math.round(usdMin * 0.92)} €`; // rough USD→EUR for "from" display

function regEntry(args: {
  slug: string; cat: MicroserviceCategory; icon: string;
  name: { en: string; fr: string };
  tagline: { en: string; fr: string };
  description: { en: string; fr: string };
  card_brief: { en: string; fr: string };
  value_prop: { en: string; fr: string };
  deliverable: { en: string; fr: string };
  visual_brief: { en: string; fr: string };
  tiers: { b: [number, number, number, number, string, string]; s: [number, number, number, number, string, string]; p: [number, number, number, number, string, string] };
}): Microservice {
  const stdMin = args.tiers.s[0];
  return {
    id: `reg-${args.slug}`, slug: args.slug,
    name: args.name, tagline: args.tagline, description: args.description,
    category: args.cat, pricing_model: "per_use",
    pricing_xof: `${usd(args.tiers.s[0], args.tiers.s[1])} USD`,
    pricing_eur: eurFrom(stdMin),
    target_countries: ["UEMOA-all"],
    icon: args.icon, featured: false,
    how_it_works: {
      en: ["Brief us in 10–20 minutes", "Operator assigned within 4h", `Delivered in ${args.tiers.s[2]}–${args.tiers.s[3]} days`, "Unlimited revisions within scope"],
      fr: ["Brief en 10–20 minutes", "Opérateur assigné sous 4 h", `Livré en ${args.tiers.s[2]}–${args.tiers.s[3]} jours`, "Révisions illimitées dans le scope"],
    },
    integrations: [],
    faq: [],
    requires_scoping_call: false,
    card_brief: args.card_brief,
    value_prop: args.value_prop,
    deliverable: args.deliverable,
    visual_brief: args.visual_brief,
    tiers: {
      basic:    { price_usd: { min: args.tiers.b[0], max: args.tiers.b[1] }, lead_days: { min: args.tiers.b[2], max: args.tiers.b[3] }, scope: { en: args.tiers.b[4], fr: args.tiers.b[5] } },
      standard: { price_usd: { min: args.tiers.s[0], max: args.tiers.s[1] }, lead_days: { min: args.tiers.s[2], max: args.tiers.s[3] }, scope: { en: args.tiers.s[4], fr: args.tiers.s[5] } },
      premium:  { price_usd: { min: args.tiers.p[0], max: args.tiers.p[1] }, lead_days: { min: args.tiers.p[2], max: args.tiers.p[3] }, scope: { en: args.tiers.p[4], fr: args.tiers.p[5] } },
    },
  };
}

microservices.push(
  regEntry({
    slug: "brand-logo", cat: "branding-design", icon: "Palette",
    name: { en: "Brand logo", fr: "Logo de marque" },
    tagline: { en: "A distinctive mark you can use anywhere", fr: "Un signe distinctif utilisable partout" },
    description: {
      en: "Original logo to establish a memorable, clean and immediately usable brand foundation.",
      fr: "Création de logo original pour poser une base de marque mémorisable, propre et exploitable très vite.",
    },
    card_brief: { en: "Scans the brand at a glance and reassures from the first pitch.", fr: "Crible la marque d'un coup d'œil et rassure dès le premier pitch." },
    value_prop: { en: "A distinctive mark you can use everywhere, with ready-to-deploy variations.", fr: "Un signe distinctif utilisable partout, avec déclinaisons prêtes à déployer selon le pack." },
    deliverable: { en: "3 concepts, SVG/PNG versions, source file (premium)", fr: "3 concepts, versions SVG/PNG, fichier source selon le pack" },
    visual_brief: { en: "Logo centered, black/color variants and business-card mockup.", fr: "Logo au centre, variantes noir/couleur et mockup carte de visite." },
    tiers: {
      b: [30, 90, 2, 3, "1 concept, PNG/SVG", "1 concept, PNG/SVG"],
      s: [90, 250, 4, 6, "3 concepts, full variants", "3 concepts, déclinaisons complètes"],
      p: [250, 700, 7, 10, "3 concepts + source file + brand snippet", "3 concepts + fichier source + mini-charte"],
    },
  }),
  regEntry({
    slug: "landing-page-design", cat: "web-mobile", icon: "Layout",
    name: { en: "Landing page design", fr: "Design de landing page" },
    tagline: { en: "A clear, fast, conversion-oriented page", fr: "Une page claire, rapide, orientée conversion" },
    description: {
      en: "Landing page focused on conversion, credibility and speed to launch.",
      fr: "Création de landing page centrée sur conversion, crédibilité et rapidité de mise en ligne.",
    },
    card_brief: { en: "Ships a clear, fast, conversion-oriented asset.", fr: "Met en ligne un actif clair, rapide et orienté conversion." },
    value_prop: { en: "An effective, responsive page ready to receive traffic.", fr: "Une page efficace, responsive et prête à recevoir du trafic." },
    deliverable: { en: "Desktop/mobile mockup focused on conversion", fr: "Maquette desktop/mobile centrée conversion" },
    visual_brief: { en: "Browser screen with strong hero, visible CTA, social proof.", fr: "Écran navigateur avec hero fort, CTA visible et preuve sociale." },
    tiers: {
      b: [150, 400, 4, 7, "1-section design, mobile", "Design 1 section, mobile"],
      s: [400, 1200, 7, 14, "Full page, desktop + mobile", "Page complète desktop + mobile"],
      p: [1200, 3200, 14, 28, "Multi-section, motion + handoff to dev", "Multi-sections, motion + handoff dev"],
    },
  }),
  regEntry({
    slug: "marketing-strategy-sprint", cat: "marketing-acquisition", icon: "Target",
    name: { en: "Marketing strategy sprint", fr: "Sprint de stratégie marketing" },
    tagline: { en: "Clarify priorities before investing more", fr: "Clarifier les priorités avant d'investir plus" },
    description: {
      en: "Decision framework and action plan to help a founder move faster with less ambiguity.",
      fr: "Cadre de décision et plan d'action pour aider un fondateur à avancer plus vite avec moins d'ambiguïté.",
    },
    card_brief: { en: "Clarify your priorities before investing more.", fr: "Clarifie les priorités avant d'investir plus." },
    value_prop: { en: "Actionable recommendation, no jargon, no useless slides.", fr: "Recommandation exploitable sans jargon ni slides inutiles." },
    deliverable: { en: "90-day plan with channels, KPIs, priorities", fr: "Plan marketing 90 jours avec canaux, KPI et priorités" },
    visual_brief: { en: "Strategic canvas with priorities, channels, KPIs.", fr: "Canvas stratégique avec priorités, canaux et KPI." },
    tiers: {
      b: [120, 300, 3, 5, "1-channel focus", "Focus 1 canal"],
      s: [300, 900, 7, 14, "Multi-channel, 90-day plan", "Multi-canaux, plan 90 jours"],
      p: [900, 2500, 14, 21, "Full GTM sprint + KPI dashboard", "Sprint GTM complet + dashboard KPI"],
    },
  }),
  regEntry({
    slug: "seo-audit", cat: "content-seo", icon: "Search",
    name: { en: "SEO audit", fr: "Audit SEO" },
    tagline: { en: "Where to gain traffic before writing more", fr: "Où gagner du trafic avant d'écrire plus" },
    description: {
      en: "Targeted SEO analysis to identify blockers, opportunities and priority actions.",
      fr: "Analyse ciblée du SEO pour identifier freins, opportunités et actions prioritaires.",
    },
    card_brief: { en: "Shows where to gain traffic before writing more content.", fr: "Montre où gagner du trafic avant d'écrire plus de contenu." },
    value_prop: { en: "Prioritized diagnosis, quick wins, actionable roadmap.", fr: "Diagnostic priorisé, quick wins et feuille de route actionnable." },
    deliverable: { en: "On-page, technical, content audit + roadmap", fr: "Audit on-page, technique, contenu et roadmap" },
    visual_brief: { en: "SEO scorecard with alerts, checkmarks, progress curve.", fr: "Scorecard SEO avec alertes, checkmarks et courbe de progression." },
    tiers: {
      b: [80, 220, 2, 4, "Top-10 quick wins", "Top-10 quick wins"],
      s: [220, 650, 5, 7, "Full on-page + technical audit", "Audit on-page + technique complet"],
      p: [650, 1800, 7, 14, "Audit + 90-day execution roadmap", "Audit + roadmap d'exécution 90 jours"],
    },
  }),
  regEntry({
    slug: "short-form-video-edit", cat: "video-creative", icon: "Video",
    name: { en: "Short-form video editing", fr: "Montage vidéo short-form" },
    tagline: { en: "Capture attention faster than long text", fr: "Capter l'attention plus vite qu'un long texte" },
    description: {
      en: "Production or editing of short-form video to capture attention and clarify the offer fast.",
      fr: "Production ou montage de vidéo short-form pour capturer l'attention vite et clarifier l'offre.",
    },
    card_brief: { en: "Captures attention faster than long text.", fr: "Capte l'attention plus vite qu'un long texte." },
    value_prop: { en: "Ready-to-publish visual content tailored to platform codes.", fr: "Contenu visuel prêt à publier, adapté aux codes des plateformes." },
    deliverable: { en: "3–30 vertical videos depending on tier", fr: "3 à 30 vidéos verticales selon le pack" },
    visual_brief: { en: "Editing timeline with captions and visible hook.", fr: "Timeline de montage avec captions et hook visible." },
    tiers: {
      b: [80, 250, 3, 5, "3 vertical edits, captions", "3 montages verticaux, captions"],
      s: [250, 800, 5, 10, "10 edits, hooks, music sync", "10 montages, hooks, sync musique"],
      p: [800, 2500, 14, 21, "30 edits + content calendar", "30 montages + calendrier de publication"],
    },
  }),
  regEntry({
    slug: "lean-prd", cat: "product-ux", icon: "FileText",
    name: { en: "Lean PRD", fr: "PRD lean" },
    tagline: { en: "Reduce product friction, accelerate learning", fr: "Réduire la friction produit, accélérer l'apprentissage" },
    description: {
      en: "Product/UX work on a lean PRD to reduce friction, prioritize better and accelerate learning.",
      fr: "Travail produit/UX sur un PRD lean pour réduire la friction, mieux prioriser et accélérer l'apprentissage.",
    },
    card_brief: { en: "Reduces product friction and accelerates learning.", fr: "Réduit la friction produit et accélère l'apprentissage." },
    value_prop: { en: "Testable deliverables that reduce product uncertainty.", fr: "Livrables testables qui réduisent l'incertitude produit." },
    deliverable: { en: "Requirements doc, objectives, user stories, scope", fr: "Document de requirements, objectifs, user stories et scope" },
    visual_brief: { en: "Product document linked to user flow and priorities.", fr: "Document produit relié à flow utilisateur et priorités." },
    tiers: {
      b: [120, 350, 3, 5, "1-feature PRD", "PRD 1 feature"],
      s: [350, 1000, 7, 14, "Multi-feature PRD + user flow", "PRD multi-features + user flow"],
      p: [1000, 2800, 14, 21, "PRD + acceptance criteria + analytics plan", "PRD + critères d'acceptation + plan analytics"],
    },
  }),
  regEntry({
    slug: "ai-audit-use-cases", cat: "data-ai", icon: "Cpu",
    name: { en: "AI audit & use cases", fr: "Audit IA et use cases" },
    tagline: { en: "Avoid gimmicky AI projects, target real gains", fr: "Éviter les projets IA gadgets, cibler les vrais gains" },
    description: {
      en: "Targeted analysis of AI use cases to identify ROI, complexity and quick wins.",
      fr: "Analyse ciblée des cas d'usage IA pour identifier ROI, complexité et quick wins.",
    },
    card_brief: { en: "Avoid gimmicky AI projects and target real gains.", fr: "Évite les projets IA gadgets et cible les vrais gains." },
    value_prop: { en: "Prioritized diagnosis and actionable roadmap.", fr: "Diagnostic priorisé et feuille de route actionnable." },
    deliverable: { en: "AI use-case map + potential ROI", fr: "Cartographie des cas d'usage IA et ROI potentiel" },
    visual_brief: { en: "Workflow map with gain points and ROI score.", fr: "Carte des workflows avec points de gain et score ROI." },
    tiers: {
      b: [80, 220, 2, 4, "Top-5 AI use cases", "Top-5 cas d'usage IA"],
      s: [220, 650, 5, 7, "Use-case map + ROI scoring", "Carte cas d'usage + scoring ROI"],
      p: [650, 1800, 7, 14, "Map + 90-day pilot plan", "Carte + plan de pilote 90 jours"],
    },
  }),
  regEntry({
    slug: "sop-pack", cat: "ops-automation", icon: "ClipboardList",
    name: { en: "SOP & procedures pack", fr: "Pack SOP et procédures" },
    tagline: { en: "Save time, reduce manual workload", fr: "Gagner du temps, réduire la charge manuelle" },
    description: {
      en: "Organize SOPs to eliminate manual tasks, clarify responsibilities and improve execution reliability.",
      fr: "Organisation des SOP pour supprimer les tâches manuelles, clarifier les responsabilités et fiabiliser l'exécution.",
    },
    card_brief: { en: "Saves time and reduces manual workload.", fr: "Fait gagner du temps et réduit la charge manuelle." },
    value_prop: { en: "Simpler operating method that is more delegable and less fragile.", fr: "Mode opératoire plus simple, plus délégable et moins fragile." },
    deliverable: { en: "Documented SOPs for 3 to 10 processes", fr: "SOP documentées pour 3 à 10 processus" },
    visual_brief: { en: "Process diagram + checklists + standardized documents.", fr: "Schéma process + checklists + documents standardisés." },
    tiers: {
      b: [80, 250, 2, 4, "3 SOPs documented", "3 SOP documentées"],
      s: [250, 700, 5, 10, "6 SOPs + checklists", "6 SOP + checklists"],
      p: [700, 2000, 14, 21, "10 SOPs + automation handoff", "10 SOP + handoff automatisation"],
    },
  }),
  regEntry({
    slug: "terms-conditions", cat: "legal-compliance", icon: "ScrollText",
    name: { en: "Terms & conditions", fr: "Termes et conditions" },
    tagline: { en: "Frame the sale, limit contractual gray areas", fr: "Cadrer la vente, limiter les zones grises" },
    description: {
      en: "Prepare T&Cs to better protect the business and frame the commercial relationship.",
      fr: "Préparation de termes et conditions pour mieux protéger l'activité et cadrer la relation commerciale.",
    },
    card_brief: { en: "Frames the sale and limits contractual gray areas.", fr: "Cadre la vente et limite les zones grises contractuelles." },
    value_prop: { en: "Documentary foundation to launch or relaunch cleanly.", fr: "Cadre documentaire de base pour lancer ou relancer proprement." },
    deliverable: { en: "T&Cs tailored to your business model", fr: "CGV/terms adaptés au modèle d'activité" },
    visual_brief: { en: "Clean contractual document with seal, clauses, signature.", fr: "Document contractuel net avec sceau, clauses et signature." },
    tiers: {
      b: [120, 350, 3, 5, "Generic T&Cs template", "CGV template générique"],
      s: [350, 1000, 5, 10, "Business-model tailored T&Cs", "CGV adaptées au modèle"],
      p: [1000, 2800, 14, 21, "T&Cs + privacy + DPA bundle", "CGV + politique de vie privée + DPA"],
    },
  }),
  regEntry({
    slug: "startup-financial-model", cat: "finance-fundraising", icon: "TrendingUp",
    name: { en: "Startup financial model", fr: "Modèle financier startup" },
    tagline: { en: "Make growth and cash readable for raising or arbitrating", fr: "Rendre croissance et trésorerie lisibles" },
    description: {
      en: "Build a financial model to manage cash, growth or a fundraising application.",
      fr: "Construction d'un modèle financier pour piloter la trésorerie, la croissance ou un dossier de financement.",
    },
    card_brief: { en: "Makes growth and cash readable for raising or arbitrating.", fr: "Rend la croissance et la trésorerie lisibles pour lever ou arbitrer." },
    value_prop: { en: "Defensible numbers for steering, negotiating, raising.", fr: "Chiffres défendables pour piloter, négocier ou lever." },
    deliverable: { en: "3–5 year model with assumptions, P&L, cash, KPIs", fr: "Modèle 3 à 5 ans avec hypothèses, P&L, cash et KPI" },
    visual_brief: { en: "CFO view with sheet, cash curve, detailed assumptions.", fr: "Vue CFO avec tableur, cash curve et hypothèses détaillées." },
    tiers: {
      b: [150, 400, 4, 7, "3-year light model", "Modèle 3 ans light"],
      s: [400, 1200, 7, 14, "5-year P&L + cash + KPIs", "5 ans P&L + cash + KPI"],
      p: [1200, 3500, 14, 28, "Investor-grade model + scenario engine", "Modèle investor-grade + moteur de scénarios"],
    },
  }),
  regEntry({
    slug: "sales-deck", cat: "sales-cs", icon: "Presentation",
    name: { en: "Sales presentation", fr: "Présentation commerciale" },
    tagline: { en: "A clean, compelling sales support", fr: "Un support commercial propre et convaincant" },
    description: {
      en: "Structure a sales presentation to open more conversations and move them forward.",
      fr: "Structuration d'une présentation commerciale pour ouvrir plus de conversations et mieux les faire avancer.",
    },
    card_brief: { en: "Equips the founder with a clean, compelling sales support.", fr: "Équipe le fondateur d'un support commercial propre et convaincant." },
    value_prop: { en: "Sales assets you can use directly.", fr: "Assets commerciaux directement utilisables." },
    deliverable: { en: "Sales deck: 10–20 slides, focused on closing", fr: "Sales deck 10 à 20 slides orienté closing" },
    visual_brief: { en: "Sales deck with benefits, proof, CTA.", fr: "Deck de vente avec bénéfices, preuve et CTA." },
    tiers: {
      b: [80, 220, 2, 4, "10-slide deck", "Deck 10 slides"],
      s: [220, 650, 4, 7, "15-slide deck + speaker notes", "Deck 15 slides + notes orateur"],
      p: [650, 1800, 7, 14, "20-slide deck + design system", "Deck 20 slides + système design"],
    },
  }),
  regEntry({
    slug: "job-description-pack", cat: "hr-talent", icon: "Users",
    name: { en: "Job description pack", fr: "Pack fiches de poste" },
    tagline: { en: "Attract the right candidates with a clear scope", fr: "Attirer les bons candidats avec un scope clair" },
    description: {
      en: "Formalize job descriptions to professionalize team management and reduce improvisation.",
      fr: "Formalisation de fiches de poste pour professionnaliser la gestion d'équipe et réduire l'improvisation.",
    },
    card_brief: { en: "Helps attract the right candidates with a clear scope from the start.", fr: "Aide à attirer les bons candidats avec un scope clair dès le début." },
    value_prop: { en: "HR foundations usable even with a small team.", fr: "Bases RH exploitables même avec une petite équipe." },
    deliverable: { en: "1 to 5 job descriptions depending on tier", fr: "1 à 5 job descriptions selon le pack" },
    visual_brief: { en: "Clean JDs with responsibilities, must-haves, reporting line.", fr: "Fiches poste nettes avec responsabilités, must-have et reporting line." },
    tiers: {
      b: [80, 220, 2, 4, "1 JD", "1 fiche de poste"],
      s: [220, 650, 4, 7, "3 JDs + scorecard", "3 fiches + scorecard"],
      p: [650, 1800, 7, 14, "5 JDs + interview kit", "5 fiches + kit d'entretien"],
    },
  }),
);

// Lazy merge: the 200-row register is appended after the curated list.
// Curated entries always win on slug collision (the ingest script suffixes -register on conflict).
import { microservicesRegister } from "./microservices-register.generated";

const _allCache: Microservice[] = (() => {
  const seen = new Set(microservices.map((m) => m.slug));
  const merged: Microservice[] = [...microservices];
  for (const r of microservicesRegister) {
    if (seen.has(r.slug)) continue;
    seen.add(r.slug);
    merged.push(r);
  }
  return merged;
})();

export function getAllMicroservices(): Microservice[] {
  return _allCache;
}

export function getMicroservice(slug: string): Microservice | undefined {
  return _allCache.find((m) => m.slug === slug);
}
